import React, { useState, useEffect, useCallback } from 'react';
import { FolderOpen, Download, FileText, File, Search, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/layout/Layout';
import mockApi from '../services/mockApi';

const ClienteDocumentosPage = () => {
  const { user } = useAuth();
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [busqueda, setBusqueda] = useState('');

  const cargarDocumentos = useCallback(async () => {
    try {
      setLoading(true);
      const clienteRes = await mockApi.getClienteByEmail(user.email);
      const response = await mockApi.getDocumentosCliente(clienteRes.data.id);
      setDocumentos(response.data || []);
    } catch (error) {
      console.error('Error al cargar documentos:', error);
      setDocumentos([]);
    } finally {
      setLoading(false);
    }
  }, [user.email]);

  useEffect(() => {
    cargarDocumentos();
  }, [cargarDocumentos]);

  const descargarDocumento = (documento) => {
    alert(`Descargando: ${documento.nombre}`);
  };

  const documentosFiltrados = documentos.filter(doc => {
    const cumpleFiltro = filtroTipo === 'todos' || doc.tipo === filtroTipo;
    const cumpleBusqueda = busqueda === '' ||
      doc.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
      doc.descripcion.toLowerCase().includes(busqueda.toLowerCase());
    return cumpleFiltro && cumpleBusqueda;
  });

  const tiposDocumento = [
    { value: 'todos', label: 'Todos', count: documentos.length },
    { value: 'certificado', label: 'Certificados', count: documentos.filter(d => d.tipo === 'certificado').length },
    { value: 'factura', label: 'Facturas', count: documentos.filter(d => d.tipo === 'factura').length },
    { value: 'informe', label: 'Informes', count: documentos.filter(d => d.tipo === 'informe').length },
    { value: 'justificante', label: 'Justificantes', count: documentos.filter(d => d.tipo === 'justificante').length }
  ];

  const getTipoConfig = (tipo) => {
    const configs = {
      certificado: { color: 'bg-info-50 text-info-700', icon: FileText, label: 'Certificado' },
      factura: { color: 'bg-success-50 text-success-700', icon: File, label: 'Factura' },
      informe: { color: 'bg-primary-50 text-primary-700', icon: FileText, label: 'Informe' },
      justificante: { color: 'bg-warning-50 text-warning-700', icon: File, label: 'Justificante' }
    };
    return configs[tipo] || configs.certificado;
  };

  const estadisticas = {
    total: documentos.length,
    certificados: documentos.filter(d => d.tipo === 'certificado').length,
    facturas: documentos.filter(d => d.tipo === 'factura').length,
    informes: documentos.filter(d => d.tipo === 'informe').length
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-700 mx-auto" style={{ animation: 'spin 1s linear infinite' }}></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Mis Documentos
            </h1>
            <p className="text-gray-500 mt-1">Accede y descarga todos tus documentos</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-6 border border-gray-200 border-l-4 border-l-primary-700 shadow-soft">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Documentos</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{estadisticas.total}</p>
              </div>
              <FolderOpen className="w-10 h-10 text-primary-700" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-gray-200 border-l-4 border-l-info-500 shadow-soft">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Certificados</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{estadisticas.certificados}</p>
              </div>
              <FileText className="w-10 h-10 text-info-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-gray-200 border-l-4 border-l-success-500 shadow-soft">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Facturas</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{estadisticas.facturas}</p>
              </div>
              <File className="w-10 h-10 text-success-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-gray-200 border-l-4 border-l-primary-500 shadow-soft">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Informes</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{estadisticas.informes}</p>
              </div>
              <FileText className="w-10 h-10 text-primary-500" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-soft">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar documentos..."
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {tiposDocumento.map(tipo => (
                <button
                  key={tipo.value}
                  onClick={() => setFiltroTipo(tipo.value)}
                  className={`px-4 py-3 rounded-lg font-semibold whitespace-nowrap ${
                    filtroTipo === tipo.value
                      ? 'bg-primary-700 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tipo.label} ({tipo.count})
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documentosFiltrados.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">No se encontraron documentos</p>
              </div>
            ) : (
              documentosFiltrados.map((documento) => {
                const tipoConfig = getTipoConfig(documento.tipo);
                const IconoTipo = tipoConfig.icon;

                return (
                  <div key={documento.id} className="bg-white rounded-lg p-6 border border-gray-200 shadow-card">
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${tipoConfig.color}`}>
                        <IconoTipo className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 mb-1 truncate">{documento.nombre}</h3>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${tipoConfig.color}`}>
                          {tipoConfig.label}
                        </span>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{documento.descripcion}</p>

                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(documento.fecha).toLocaleDateString()}</span>
                      <span className="mx-2">·</span>
                      <span>{documento.tamaño}</span>
                    </div>

                    <button
                      onClick={() => descargarDocumento(documento)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-700 text-white rounded-lg hover:bg-primary-800 font-semibold"
                    >
                      <Download className="w-4 h-4" />
                      <span>Descargar</span>
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="bg-info-50 rounded-lg p-6 border border-info-200">
          <div className="flex items-start gap-4">
            <FileText className="w-6 h-6 text-info-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-info-900 mb-2">Información sobre tus documentos</h3>
              <ul className="text-sm text-info-700 space-y-2">
                <li>Los documentos están disponibles durante toda la vigencia de tu garantía</li>
                <li>Puedes descargar cada documento las veces que necesites</li>
                <li>Los certificados y facturas son documentos oficiales válidos</li>
                <li>Si necesitas un documento específico que no aparece, contacta con soporte</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ClienteDocumentosPage;
